import { computed, ref, reactive, watch, onMounted } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import {
  getAlbumFileList as getAlbumFileListApi,
  getAlbumList as getAlbumListApi,
  deleteAlbumFile as deleteAlbumFileApi,
  TDeleteAlbumFileParams,
} from '@remote/api/adapter/shell-mqtt'
import { useBaseStore } from '@remote/store/base'
import { store } from '@remote/store'
import { IAlbumItem, IFormateDayDimensionDataItem, IAlbumListItem } from '@remote/type/album'
import { formateDataByDay, getSubPathAlbumAllFileList, downloadPhoto } from './helper'
import { toast, openPreviewAlbum } from '@remote/utils/dialog'
import { pathJoin } from '@remote/utils/file-helper'

type AlbumTab = 'PHOTO' | 'ALBUM'

enum EAlbumTab {
  PHOTO,
  ALBUM
}

const ALBUM_TABS: readonly { id: AlbumTab; title: string }[] = [
  {
    id: 'PHOTO',
    title: '照片',
  },
  {
    id: 'ALBUM',
    title: '相册',
  },
]

const initData = {
  // 照片列表
  all: {} as { [md5sum: string]: IAlbumItem }, // 缓存文件
  list: [] as IAlbumItem[],
  total: 0,
  displayList: [] as IFormateDayDimensionDataItem[],
  pageToken: '',
  isLoading: true,
  errorMsg: '',
  selecteds: [] as string[],
  selectedMap: {} as { [md5sum: string]: IAlbumItem },
  selectedAll: false,
  isRefresh: false,

  // 相册目录列表
  albumList: [] as IAlbumListItem[],
  albumListNums: 0,
  albumlistPageToken: '',

}

const initInSubPathAlbumData = {
  // 相册目录下的照片列表
  subPath: '',
  title: '',
  list: [] as IAlbumItem[],
  total: 0,
  displayList: [] as IFormateDayDimensionDataItem[],
  pageToken: '',
  isLoading: true,
  errorMsg: '',
  selecteds: [] as string[],
  selectedMap: {} as { [md5sum: string]: IAlbumItem },
  selectedAll: false,
}

export const useAlbumStore = defineStore('album', () => {
  const currentSlideIndex = ref(0)
  const albumTabs = ref(ALBUM_TABS)

  /** 是否正在刷新 */
  const isRefresh = ref(false)
  function setRefresh (playing: boolean) {
    isRefresh.value = playing
  }

  const data = reactive(initData)
  const subPathData = reactive(initInSubPathAlbumData)

  const getAlbumFileList = async () => {
    try {
      data.isLoading = true
      const pageToken = data.pageToken
      const params = {
        platform: 'pc',
        sub_path: '',
        media: 0,
        page_size: 100,
        page_token: pageToken,
      }
      const res = await getAlbumFileListApi(params)
      let list: IAlbumItem[] = []
      console.log('getAlbumFileList res', res)
      if (res.ret === 0) {
        data.total = res.total
        data.pageToken = res.page_token
        if (res.result?.length) {
          list = res.result
        }
      } else {
        // data.errorMsg = res.msg
      }
      data.list = data.isRefresh || !params.page_token ? list : data.list.concat(list)
      data.displayList = formateDataByDay(data.list)
    } catch (err) {
      console.log(err)
      data.errorMsg = err
    } finally {
      data.isLoading = false
      console.log('finally', data.isLoading, data.list?.length)
    }
  }

  const getAlbumList = async () => {
    try {
      data.isLoading = true
      const pageToken = data.albumlistPageToken
      const params = {
        platform: 'pc',
        page_size: 100,
        page_token: pageToken,
      }
      const res = await getAlbumListApi(params)
      let list: IAlbumListItem[] = []

      console.log('getAlbumList res', res)
      if (res.ret === 0 || res?.result?.length) {
        data.albumListNums = res.total
        data.albumlistPageToken = res.page_token
        if (res.result?.length) {
          list = res.result
        }
      }
      console.log('getAlbumList isRefresh', data.isRefresh)
      data.albumList = data.isRefresh || !params.page_token ? list : data.albumList.concat(list)
    } catch (err) {
      console.log(err)
      data.errorMsg = err
    } finally {
      data.isLoading = false
    }
  }

  const getSubPathAlbumFileList = async () => {
    try {
      subPathData.isLoading = true
      const pageToken = subPathData.pageToken
      const params = {
        platform: 'pc',
        sub_path: subPathData.subPath,
        media: 0,
        page_size: 100,
        page_token: pageToken,
      }
      const res = await getAlbumFileListApi(params)
      let list: IAlbumItem[] = []

      console.log('getSubPathAlbumFileList res', res)
      if (res.ret === 0) {
        subPathData.total = res.total
        subPathData.pageToken = res.page_token
        if (res.result?.length) {
          list = res.result
        }
      } else {
        // data.errorMsg = res.msg
      }
      subPathData.list = data.isRefresh || !params.page_token ? list : subPathData.list.concat(list)
      subPathData.displayList = formateDataByDay(subPathData.list)
    } catch (err) {
      console.log(err)
      subPathData.errorMsg = err
    } finally {
      subPathData.isLoading = false
    }
  }

  const intSubPathData = () => {
    subPathData.total = 0
    subPathData.list = []
    subPathData.displayList = []
    subPathData.pageToken = ''
    subPathData.selectedAll = false
    subPathData.selectedMap = {}
  }

  const setSubPath = (path: string, title: string) => {
    intSubPathData()
    subPathData.subPath = path
    subPathData.title = title
    getSubPathAlbumFileList()
    // 重置选中状态
    data.selectedMap = {}
    data.selectedAll = false
  }

  const toggleSelected = (photo: IAlbumItem) => {
    const dataSource = subPathData.subPath ? subPathData : data

    const nextState = !dataSource.selectedMap[photo.md5sum]
    if (nextState) {
      dataSource.selectedMap[photo.md5sum] = photo
      dataSource.selectedAll = dataSource.list.length === Object.keys(dataSource.selectedMap).length
    } else {
      dataSource.selectedAll = false
      delete dataSource.selectedMap[photo.md5sum]
    }
  }

  const toggleSelectedAll = () => {
    const dataSource = subPathData.subPath ? subPathData : data
    const nextSelectedAll = !dataSource.selectedAll
    dataSource.selectedAll = nextSelectedAll
    if (!nextSelectedAll) {
      dataSource.selectedMap = {}
    } else {
      dataSource.list.forEach(item => {
        dataSource.selectedMap[item.md5sum] = item
      })
    }
  }


  const cancelSelectedAll = () => {
    const dataSource = subPathData.subPath ? subPathData : data
    dataSource.selectedMap = {}
    dataSource.selectedAll = false
  }

  const loadMore = () => {
    console.log('loadMore album')
    if (subPathData.subPath) {
      if (!subPathData.isLoading && subPathData.pageToken) getSubPathAlbumFileList()
    } else if (currentSlideIndex.value === EAlbumTab.PHOTO) {
      if (!data.isLoading && data.pageToken) getAlbumFileList()
    } else {
      if (!data.isLoading && data.albumlistPageToken) getAlbumList()
    }
  }

  const initAlbumFileList = async () => {
    data.pageToken = ''
    data.selectedMap = {}
    data.selectedAll = false
    await getAlbumFileList()
  }

  const initAlbumList = async () => {
    data.albumlistPageToken = ''
    await getAlbumList()
  }

  const initSubPathAlbumFileList = async () => {
    subPathData.pageToken = ''
    subPathData.selectedMap = {}
    subPathData.selectedAll = false
    await getSubPathAlbumFileList()
  }
  
  const init = async () => {
    if (subPathData.subPath) {
      await initSubPathAlbumFileList()
    } else {
      await initAlbumFileList()
      await initAlbumList()
    }
  }

  const refresh = async () => {
    data.isRefresh = true
    await init()
    data.isRefresh = false
  }

  /**
   * 选择相册中展示的Tab
   */
  const selectTab = (index: number) => {
    console.log('index', index)
    currentSlideIndex.value = index
  }

  /**
   * 调用删除相册
   * 接口返回成功后，删除对应列表中的项。
   */
  const deletePhotos = async (path?: string):Promise<string|null> => {
    toast({ type: 'loading', message: '相册删除中...' })
    const params = {platform: 'pc', paths: [], keys: []} as TDeleteAlbumFileParams
    const markRemovedMap:{[md5sum: string]: boolean} = {}

    if (path) {
      params.paths.push({path, media: 0})
    } else {
      const dataSource = subPathData.subPath ? subPathData.selectedMap : data.selectedMap
      const subPath = subPathData.subPath ? subPathData.subPath : ''
      for (const md5sum in dataSource) {
        const photo = dataSource[md5sum]
        markRemovedMap[md5sum] = true
        params.keys.push({
          path: pathJoin(subPath, photo.rel_path),
          key: photo.key,
        })
      }
    }

    return deleteAlbumFileApi(params)
      .then(() => {
        toast({ type: 'success', message: '相册删除成功' })
        // 删除对应的照片或目录
        if (!path) {
          subPathData.selectedMap = {}
          subPathData.selectedAll = false
          data.selectedMap = {}
          data.selectedAll = false
          if (subPathData.subPath) {
            subPathData.list = subPathData.list.filter(item => !markRemovedMap[item.md5sum])
            subPathData.displayList = formateDataByDay(subPathData.list)
            subPathData.total = subPathData.total - Object.keys(markRemovedMap).length

            data.list = data.list.filter(item => !markRemovedMap[item.md5sum])
            data.displayList = formateDataByDay(data.list)
            data.total = data.total - Object.keys(markRemovedMap).length
          } else {
            data.list = data.list.filter(item => !markRemovedMap[item.md5sum])
            data.displayList = formateDataByDay(data.list)
            data.total = data.total - Object.keys(markRemovedMap).length
          }
          initAlbumList()
        } else {
          refresh()
        }
        return null
      })
      .catch(() => {
        toast({ type: 'error', message: '相册删除失败' })
        return '相册删除失败'
      })
  }

  /**
   * 调用删除相册
   * 接口返回成功后，删除对应列表中的项。
   */
  const deletePhoto = async (photo: IAlbumItem):Promise<string|null> => {
    toast({ type: 'loading', message: '相册删除中...' })
    const params = {platform: 'pc', paths: [], keys: []} as TDeleteAlbumFileParams
    const subPath = subPathData.subPath ? subPathData.subPath : ''
    params.keys.push({
      path: pathJoin(subPath, photo.rel_path),
      key: photo.key,
    })

    return deleteAlbumFileApi(params)
      .then(() => {
        toast({ type: 'success', message: '相册删除成功' })
        // 删除对应的照片
        subPathData.selectedMap = {}
        subPathData.selectedAll = false
        data.selectedMap = {}
        data.selectedAll = false
        if (subPathData.subPath) {
          subPathData.list = subPathData.list.filter(item => item.md5sum !== photo.md5sum)
          subPathData.displayList = formateDataByDay(subPathData.list)
          subPathData.total = subPathData.total - 1

          data.list = data.list.filter(item => item.md5sum !== photo.md5sum)
          data.displayList = formateDataByDay(data.list)
          data.total = data.total - 1
        } else {
          data.list = data.list.filter(item => item.md5sum !== photo.md5sum)
          data.displayList = formateDataByDay(data.list)
          data.total = data.total - 1
        }
        initAlbumList()
        return null
      })
      .catch(() => {
        toast({ type: 'error', message: '相册删除失败' })
        return '相册删除失败'
      })
  }

  /**
   * 调用下载相册
   */
  const downloadPhotos = async (subPath?: string) => {
    toast({ type: 'loading', message: '下载任务创建中...' })
    let dataSource: { [key: string]: IAlbumItem } | IAlbumItem[] = {}
    if (subPath) {
      // 下载相册目录下所有文件
      const [error, list] = await getSubPathAlbumAllFileList(subPath)
      if (error === 0) {
        dataSource = list
      } else {
        toast({ type: 'error', message: `下载任务创建失败[${error}]` })
      }
    } else {
      dataSource = subPathData.subPath ? subPathData.selectedMap : data.selectedMap
      // 下载选中文件
    }

    toast({ type: 'success', message: '下载任务创建成功' })
    for (const md5sum in dataSource) {
      await downloadPhoto(dataSource[md5sum], subPath)
    }
    subPathData.selectedMap = {}
    subPathData.selectedAll = false
    data.selectedMap = {}
    data.selectedAll = false
  }

  /**
   * 相册预览
   */
  const previewAlbum = (item:IAlbumItem) => {
    openPreviewAlbum({
      isShowPreviewFileComp: true,
      previewFileInfo: item,
    })
  }

  const baseStore = useBaseStore()
  const target = computed(() => {
    return store.state.curDeviceRunner?.target
  })

  const router = useRouter()
  router.afterEach((to, from) => {
    if (from.name === 'albumPath') {
      intSubPathData()
      subPathData.subPath = ''
      subPathData.title = ''
    }
  })

  watch(target, async (val) => {
    if (val) {
      init()
      console.log('store.state.user', store.state.Users)
    }
  })

  onMounted(async () => {
    await baseStore.untilTargetReady()
    init()
    console.log('store.state.user', store.state.user)
  })


  return {
    albumTabs,
    setRefresh,
    selectTab,
    data,
    loadMore,
    subPathData,
    setSubPath,
    getSubPathAlbumFileList,
    refresh,
    currentSlideIndex,
    toggleSelected,
    toggleSelectedAll,
    cancelSelectedAll,
    deletePhotos,
    deletePhoto,
    downloadPhotos,
    previewAlbum,
  }
})